'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { remember: false } });

  // Backend integration point: POST /api/admin/auth/login
  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        try {
          localStorage.setItem(
            'desiadmin_session',
            JSON.stringify({ token: json.token, admin: json.admin })
          );
        } catch {
          /* localStorage niet beschikbaar — doorgaan met inloggen */
        }
        toast.success(json.message ?? 'Ingelogd als Super Admin');
        window.location.href = '/dashboard';
      } else {
        toast.error(json.message ?? 'Ongeldig e-mailadres of wachtwoord');
      }
    } catch {
      toast.error('Verbinding mislukt. Probeer het opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-sm">
      <div className="flex items-center gap-2 mb-8 lg:hidden">
        <AppLogo size={36} />
        <span className="font-800 text-xl text-gradient-primary">DesiAdmin</span>
      </div>

      <div className="mb-8">
        <h1 className="text-2xl font-800 text-foreground mb-1.5">Inloggen</h1>
        <p className="text-sm text-muted-foreground">Voer uw beheerdersgegevens in</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate suppressHydrationWarning>
        <div suppressHydrationWarning>
          <label htmlFor="email" className="block text-sm font-600 text-foreground mb-1.5">
            E-mailadres
          </label>
          <input
            id="email"
            type="email"
            placeholder="admin@desidating.nl"
            autoComplete="email"
            className={`w-full px-4 py-2.5 text-sm bg-input border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring transition-colors
              ${errors.email ? 'border-danger' : 'border-border'}`}
            {...register('email', {
              required: 'E-mailadres is verplicht',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ongeldig e-mailadres' },
            })}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-danger font-500">{errors.email.message}</p>
          )}
        </div>

        <div suppressHydrationWarning>
          <label htmlFor="password" className="block text-sm font-600 text-foreground mb-1.5">
            Wachtwoord
          </label>
          <div className="relative" suppressHydrationWarning>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="current-password"
              className={`w-full px-4 py-2.5 pr-10 text-sm bg-input border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring transition-colors
                ${errors.password ? 'border-danger' : 'border-border'}`}
              {...register('password', {
                required: 'Wachtwoord is verplicht',
                minLength: { value: 6, message: 'Minimaal 6 tekens' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-danger font-500">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-border text-primary focus:ring-ring"
              {...register('remember')}
            />
            <span className="text-sm text-muted-foreground">Onthoud mij</span>
          </label>
          <button type="button" className="text-sm text-primary font-600 hover:underline">
            Wachtwoord vergeten?
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 text-sm font-700 text-white gradient-primary rounded-xl hover:opacity-90 active:scale-95 transition-all duration-150 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin text-white" />
              Inloggen...
            </>
          ) : (
            'Inloggen als Beheerder'
          )}
        </button>
      </form>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        Geen toegang?{' '}
        <a
          href="mailto:shervin001@gmail.com"
          className="text-primary font-600 cursor-pointer hover:underline"
        >
          Neem contact op met de systeembeheerder
        </a>
      </p>

      {/* Demo credentials */}
      <div className="mt-6 p-4 rounded-xl border border-border bg-background/50">
        <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide mb-3">
          Demo inloggegevens
        </p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground w-20 shrink-0">Gebruiker:</span>
            <code className="text-xs text-foreground font-600 bg-input px-2 py-1 rounded-lg flex-1 select-all cursor-text">
              admin@desidating.nl
            </code>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground w-20 shrink-0">Wachtwoord:</span>
            <code className="text-xs text-foreground font-600 bg-input px-2 py-1 rounded-lg flex-1 select-all cursor-text">
              Admin2026!
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}